import { useMemo, useRef, useState } from 'react';
import { Pipette, SunMedium } from 'lucide-react';
import {
  BRIGHTEN_CURVE,
  activeClipGrade,
  autoWhiteBalanceFromImageData,
  clipGradeOrIdentity,
  curveSamples,
  setClipGrade,
  type ClipGrade,
  type CurveChannel,
  type CurvePoint,
  type EditorClip,
  type EditorState,
} from '@rehub/propertycut-timeline';

const CHANNELS: Array<{ id: CurveChannel; label: string; stroke: string }> = [
  { id: 'rgb', label: 'Master', stroke: '#5eead4' },
  { id: 'r', label: 'R', stroke: '#fb7185' },
  { id: 'g', label: 'G', stroke: '#34d399' },
  { id: 'b', label: 'B', stroke: '#38bdf8' },
];

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function svgPoint(event: { currentTarget: SVGSVGElement; clientX: number; clientY: number }): CurvePoint {
  const rect = event.currentTarget.getBoundingClientRect();
  const x = ((event.clientX - rect.left) / Math.max(1, rect.width)) * 255;
  const y = 255 - ((event.clientY - rect.top) / Math.max(1, rect.height)) * 255;
  return { x: clamp(x, 0, 255), y: clamp(y, 0, 255) };
}

function nearestPoint(points: CurvePoint[], point: CurvePoint): number {
  let best = 0;
  let bestDist = Infinity;
  points.forEach((candidate, index) => {
    const dist = Math.hypot(candidate.x - point.x, candidate.y - point.y);
    if (dist < bestDist) {
      best = index;
      bestDist = dist;
    }
  });
  return bestDist <= 14 ? best : -1;
}

export type SampledFrame = ImageData | 'blocked' | 'missing';

export default function ClipGradePanel({
  clip,
  onEdit,
  onLiveEdit,
  onSampleClip,
  picking,
  onPick,
  note,
  onNote,
}: {
  clip: EditorClip;
  onEdit: (recipe: (state: EditorState) => EditorState) => void;
  onLiveEdit: (recipe: (state: EditorState) => EditorState, commit: boolean) => void;
  onSampleClip: (clipId: string) => Promise<SampledFrame>;
  picking: boolean;
  onPick: (clipId: string | null) => void;
  note: string | null;
  onNote: (note: string | null) => void;
}) {
  const [channel, setChannel] = useState<CurveChannel>('rgb');
  const [busy, setBusy] = useState(false);
  const drag = useRef<number | null>(null);
  const grade = clipGradeOrIdentity(clip.grade);
  const active = Boolean(activeClipGrade(clip.grade));
  const points = grade[channel];
  const pointsRef = useRef(points);
  pointsRef.current = points;
  const samples = useMemo(() => curveSamples(points), [points]);
  const stroke = CHANNELS.find((item) => item.id === channel)?.stroke ?? '#5eead4';

  const write = (next: ClipGrade, commit: boolean) => {
    const recipe = (state: EditorState) => setClipGrade(state, clip.id, next);
    if (commit) onEdit(recipe);
    else onLiveEdit(recipe, false);
  };

  const withGrade = (patch: Partial<ClipGrade>, commit: boolean) => {
    const apply = (state: EditorState) => {
      const current = state.clips.find((item) => item.id === clip.id);
      return setClipGrade(state, clip.id, { ...clipGradeOrIdentity(current?.grade), ...patch });
    };
    if (commit) onLiveEdit(apply, true);
    else onLiveEdit(apply, false);
  };

  const movePoint = (index: number, point: CurvePoint, commit: boolean) => {
    const current = pointsRef.current;
    if (index < 0 || index >= current.length) return;
    const last = current.length - 1;
    const minX = index === 0 ? 0 : current[index - 1].x + 1;
    const maxX = index === last ? 255 : current[index + 1].x - 1;
    const next = current.map((item, i) =>
      i === index
        ? {
            x: index === 0 || index === last ? item.x : clamp(Math.round(point.x), minX, maxX),
            y: clamp(Math.round(point.y), 0, 255),
          }
        : item,
    );
    pointsRef.current = next;
    withGrade({ [channel]: next }, commit);
  };

  const auto = async () => {
    setBusy(true);
    onNote(null);
    try {
      const frame = await onSampleClip(clip.id);
      if (frame === 'missing') {
        onNote('Move the playhead onto this clip, then try again.');
        return;
      }
      if (frame === 'blocked') {
        onNote('Use the sliders — this clip can’t be read automatically.');
        return;
      }
      const balance = autoWhiteBalanceFromImageData(frame.data);
      onEdit((state) => {
        const current = state.clips.find((item) => item.id === clip.id);
        return setClipGrade(state, clip.id, { ...clipGradeOrIdentity(current?.grade), ...balance });
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 rounded-lg border border-slate-800 bg-slate-900/60 p-2.5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] uppercase tracking-wide text-slate-500">Light and colour</p>
        {active && (
          <button
            type="button"
            onClick={() => {
              onNote(null);
              onEdit((state) => setClipGrade(state, clip.id, null));
            }}
            className="text-[11px] text-slate-400 hover:text-slate-200"
          >
            Reset
          </button>
        )}
      </div>
      <p className="text-[11px] text-slate-500">
        Lift the curve to brighten. White balance fixes a warm or cool cast. The exported video uses the same adjustment.
      </p>

      <div className="flex gap-1">
        {CHANNELS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setChannel(item.id)}
            className={`flex-1 rounded-md py-1 text-[11px] border ${
              channel === item.id ? 'border-teal-400 text-teal-100' : 'border-slate-700 text-slate-400'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <svg
        viewBox="0 0 255 255"
        className="w-full h-36 touch-none rounded-md bg-slate-950 border border-slate-800"
        role="img"
        aria-label={`${channel === 'rgb' ? 'Master' : channel.toUpperCase()} curve. Drag a point to brighten or darken.`}
        onPointerDown={(event) => {
          const point = svgPoint(event);
          const hit = nearestPoint(points, point);
          if (hit >= 0) {
            drag.current = hit;
          } else if (points.length < 8) {
            const next = [...points, { x: Math.round(point.x), y: Math.round(point.y) }].sort((a, b) => a.x - b.x);
            const index = next.findIndex((item) => item.x === Math.round(point.x));
            drag.current = index;
            pointsRef.current = next;
            withGrade({ [channel]: next }, false);
          } else {
            return;
          }
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          if (drag.current == null) return;
          movePoint(drag.current, svgPoint(event), false);
        }}
        onPointerUp={(event) => {
          if (drag.current == null) return;
          movePoint(drag.current, svgPoint(event), true);
          drag.current = null;
        }}
        onDoubleClick={(event) => {
          const hit = nearestPoint(points, svgPoint(event));
          if (hit <= 0 || hit >= points.length - 1) return;
          withGrade({ [channel]: points.filter((_, index) => index !== hit) }, true);
        }}
      >
        <line x1="0" y1="255" x2="255" y2="0" stroke="#334155" strokeWidth="1" />
        <polyline
          fill="none"
          stroke={stroke}
          strokeWidth="2"
          points={samples.map((point) => `${point.x},${255 - point.y}`).join(' ')}
        />
        {points.map((point, index) => (
          <circle
            key={`${point.x}-${index}`}
            cx={point.x}
            cy={255 - point.y}
            r={index === 0 || index === points.length - 1 ? 5 : 6}
            fill={stroke}
            stroke="#0f172a"
            strokeWidth="1.5"
          />
        ))}
      </svg>
      <p className="text-[11px] text-slate-500">Drag a point up to brighten. Double-click a point to remove it.</p>
      <button
        type="button"
        onClick={() => {
          onNote(null);
          write({ ...grade, rgb: BRIGHTEN_CURVE.map((point) => ({ ...point })) }, true);
          setChannel('rgb');
        }}
        className="w-full flex items-center justify-center gap-1.5 rounded-md border border-slate-700 py-1.5 text-xs text-slate-200 hover:border-teal-400"
      >
        <SunMedium className="w-3.5 h-3.5 text-teal-200" />
        Brighten
      </button>

      <label className="block text-[11px] text-slate-500">
        White balance · {grade.temperature > 8 ? 'Warmer' : grade.temperature < -8 ? 'Cooler' : 'Neutral'}
        <input
          type="range"
          min={-100}
          max={100}
          step={1}
          value={grade.temperature}
          aria-label="White balance temperature"
          onChange={(event) => withGrade({ temperature: Number(event.target.value) }, false)}
          onPointerUp={(event) => withGrade({ temperature: Number(event.currentTarget.value) }, true)}
          onKeyUp={(event) => withGrade({ temperature: Number(event.currentTarget.value) }, true)}
          onBlur={(event) => withGrade({ temperature: Number(event.currentTarget.value) }, true)}
          className="mt-1 w-full accent-teal-400"
        />
      </label>
      <label className="block text-[11px] text-slate-500">
        Tint · {grade.tint > 8 ? 'Magenta' : grade.tint < -8 ? 'Green' : 'Neutral'}
        <input
          type="range"
          min={-100}
          max={100}
          step={1}
          value={grade.tint}
          aria-label="White balance tint"
          onChange={(event) => withGrade({ tint: Number(event.target.value) }, false)}
          onPointerUp={(event) => withGrade({ tint: Number(event.currentTarget.value) }, true)}
          onKeyUp={(event) => withGrade({ tint: Number(event.currentTarget.value) }, true)}
          onBlur={(event) => withGrade({ tint: Number(event.currentTarget.value) }, true)}
          className="mt-1 w-full accent-teal-400"
        />
      </label>
      <div className="grid grid-cols-2 gap-1.5">
        <button
          type="button"
          disabled={busy}
          onClick={() => void auto()}
          className="rounded-md border border-slate-700 py-1.5 text-[11px] text-slate-200 hover:border-teal-400 disabled:opacity-50"
        >
          {busy ? 'Reading…' : 'Auto white balance'}
        </button>
        <button
          type="button"
          onClick={() => {
            onNote(null);
            onPick(picking ? null : clip.id);
          }}
          className={`rounded-md border py-1.5 text-[11px] flex items-center justify-center gap-1 ${
            picking ? 'border-teal-400 text-teal-100' : 'border-slate-700 text-slate-200 hover:border-teal-400'
          }`}
        >
          <Pipette className="w-3 h-3" />
          {picking ? 'Cancel pick' : 'Pick neutral'}
        </button>
      </div>
      {picking && (
        <p className="text-[11px] text-teal-200">Click a white wall or grey surface in the preview. Esc cancels.</p>
      )}
      {note && <p className="text-[11px] text-amber-200">{note}</p>}
    </div>
  );
}
